import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, Sparkles, Copy } from "lucide-react";
import { useLang } from "../LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = "queensway_exit_intent_v1";

/**
 * Sticky exit-intent popup.
 * - Triggers when the cursor leaves the viewport from the top edge.
 * - Triggers max once per device (gated by localStorage).
 * - Also gated to "first visit" — won't show if user has already booked.
 * - Skipped on touch / mobile (no mouseout from top there).
 */
export default function ExitIntentPopup() {
  const { lang } = useLang();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [code, setCode] = useState("");
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Skip if already shown / dismissed / converted on this device
    if (localStorage.getItem(STORAGE_KEY)) return;
    // Skip on coarse pointers (mobile/tablet)
    if (window.matchMedia("(pointer: coarse)").matches) return;

    // Only arm after the user has been on the page for a moment
    let armed = false;
    const armTimer = setTimeout(() => { armed = true; }, 12000);

    const onMouseOut = (e) => {
      if (!armed) return;
      // Only trigger when leaving from the top edge
      if (e.clientY > 0) return;
      if (e.relatedTarget || e.toElement) return;
      setOpen(true);
      localStorage.setItem(STORAGE_KEY, "shown:" + new Date().toISOString());
      document.removeEventListener("mouseout", onMouseOut);
    };

    document.addEventListener("mouseout", onMouseOut);
    return () => {
      clearTimeout(armTimer);
      document.removeEventListener("mouseout", onMouseOut);
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/exit-intent`, { email, language: lang });
      setCode(res.data.code);
    } catch {
      setError(lang === "fr" ? "Une erreur est survenue." : "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  if (!open) return null;

  const titleEN = "Wait — a little gift.";
  const titleFR = "Un instant — un petit cadeau.";
  const bodyEN = "Leave us your email and we'll send a private 5% off code for your first visit.";
  const bodyFR = "Laissez-nous votre email et recevez un code privé de 5% sur votre première visite.";

  return (
    <div
      data-testid="exit-intent-popup"
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 bg-background/85 backdrop-blur-md qw-fade-in"
    >
      <div className="relative max-w-md w-full bg-background border border-primary/40 shadow-2xl p-10 md:p-12">
        <button
          onClick={() => setOpen(false)}
          data-testid="exit-intent-close"
          aria-label="Close"
          className="absolute top-4 right-4 text-foreground/60 hover:text-primary transition-colors"
        >
          <X size={20} strokeWidth={1.2} />
        </button>

        <Sparkles size={22} strokeWidth={0.8} className="text-primary mb-5" />
        <p className="qw-overline mb-3">{lang === "fr" ? "Offre privée" : "Private offer"}</p>

        {!code ? (
          <>
            <h3 className="font-serif text-3xl md:text-4xl text-foreground font-light leading-tight mb-4">
              {lang === "fr" ? titleFR : titleEN}
            </h3>
            <p className="text-sm text-muted-foreground mb-7 leading-relaxed">
              {lang === "fr" ? bodyFR : bodyEN}
            </p>
            <form onSubmit={submit} className="space-y-5">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={lang === "fr" ? "Votre email" : "Your email"}
                className="qw-input"
                data-testid="exit-intent-email-input"
                autoFocus
              />
              {error && <p className="text-xs text-destructive">{error}</p>}
              <button
                type="submit"
                disabled={submitting}
                data-testid="exit-intent-submit"
                className="qw-btn qw-btn-primary w-full justify-center disabled:opacity-60"
              >
                {submitting
                  ? (lang === "fr" ? "Envoi…" : "Sending…")
                  : (lang === "fr" ? "Recevoir mon code" : "Get my code")}
              </button>
              <p className="text-[0.65rem] text-muted-foreground tracking-wider text-center">
                {lang === "fr" ? "Nous respectons votre vie privée." : "We respect your privacy."}
              </p>
            </form>
          </>
        ) : (
          <>
            <h3 className="font-serif text-3xl md:text-4xl text-foreground font-light leading-tight mb-4">
              {lang === "fr" ? "Votre code est prêt." : "Your code is ready."}
            </h3>
            <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
              {lang === "fr"
                ? "Mentionnez-le lors de votre première réservation pour recevoir 5% de réduction."
                : "Mention it when you book your first visit to get 5% off."}
            </p>
            <div className="flex items-center justify-between border border-primary/40 bg-primary/5 px-5 py-4 mb-6">
              <p data-testid="exit-intent-code" className="font-serif text-2xl text-primary qw-num tracking-widest">{code}</p>
              <button
                onClick={copy}
                data-testid="exit-intent-copy"
                className="text-[0.65rem] tracking-wider uppercase text-foreground/70 hover:text-primary flex items-center gap-1"
              >
                <Copy size={11} strokeWidth={1.2} />
                {copied ? (lang === "fr" ? "Copié" : "Copied") : (lang === "fr" ? "Copier" : "Copy")}
              </button>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="exit-intent-done"
              className="qw-btn qw-btn-outline w-full justify-center"
            >
              {lang === "fr" ? "Continuer la visite" : "Continue browsing"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
